// SortableList — the reusable accessible reorder primitive (ADR-046). The ONLY
// dnd-kit consumer: we own the markup + tokens (the React-Aria posture), dnd-kit
// owns the drag mechanics + the keyboard sensor + screen-reader announcements.
//
// Controlled: the consumer holds the order (`items`) and updates it from
// `onReorder` (commit-on-save lives in the consumer). Drag is **handle-activated**
// — only the grip starts a drag, so row content stays selectable and the keyboard
// path is a real focusable control. Reorder is "ordered siblings within a scope"
// (ADR-046): this component is scope-agnostic; the consumer passes whichever
// sibling set it is reordering (a project's site types, an area's children —
// through `ReorderDrawer` — or a responsibility's chain).

import { type ReactNode } from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
  type UniqueIdentifier,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { icons } from './icons.js';

export interface SortableListProps<T> {
  /** Current order; the consumer updates it from `onReorder`. */
  items: T[];
  /** Stable id per item (the sort key + React key). */
  getId: (item: T) => UniqueIdentifier;
  /** Called on drop with the reordered array. */
  onReorder: (items: T[]) => void;
  /** Row content (the consumer's display — name, badges, …). */
  renderItem: (item: T) => ReactNode;
  /** Accessible name for the reorderable list. */
  ariaLabel: string;
  /** Accessible name for each row's drag handle, e.g. `Reorder ${name}`. */
  handleLabel: (item: T) => string;
}

const handleClass =
  'flex shrink-0 cursor-grab touch-none items-center justify-center rounded-md p-1.5 ' +
  'text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)] hover:text-[var(--color-text-primary)] ' +
  'outline-none focus-ring active:cursor-grabbing';

function SortableRow({
  id,
  handleLabel,
  children,
}: {
  id: UniqueIdentifier;
  handleLabel: string;
  children: ReactNode;
}) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } =
    useSortable({ id });

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={
        'flex items-center gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-3 py-2 ' +
        (isDragging ? 'relative z-overlay shadow-modal' : '')
      }
    >
      <button
        ref={setActivatorNodeRef}
        type="button"
        aria-label={handleLabel}
        className={handleClass}
        {...attributes}
        {...listeners}
      >
        <icons.drag size={18} aria-hidden />
      </button>
      <div className="min-w-0 flex-1">{children}</div>
    </li>
  );
}

export function SortableList<T>({
  items,
  getId,
  onReorder,
  renderItem,
  ariaLabel,
  handleLabel,
}: SortableListProps<T>) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const ids = items.map(getId);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = ids.indexOf(active.id);
    const to = ids.indexOf(over.id);
    if (from === -1 || to === -1) return;
    onReorder(arrayMove(items, from, to));
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <ul aria-label={ariaLabel} className="flex flex-col gap-2">
          {items.map((item) => {
            const id = getId(item);
            return (
              <SortableRow key={String(id)} id={id} handleLabel={handleLabel(item)}>
                {renderItem(item)}
              </SortableRow>
            );
          })}
        </ul>
      </SortableContext>
    </DndContext>
  );
}
